import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Trans } from "@lingui/macro";
import { toast } from "react-toastify";
import parseErrorMessage from "helpers-js/parseErrorMessage";
import { transformMyRestApiErrorsToAnObject } from "../../../utils";
import DeleteResourceButton from "../DeleteResourceButton";
import Checkbox from "../Checkbox";
import { ApiConsumer } from "../../../contexts/ApiContext";
import { updateVideoHost, createVideoHost } from "../../../api";
import { Button } from "@iforwms/react-components";

const VideoHostSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, <Trans>Please enter a longer name (min: 2 characters)</Trans>)
    .max(50, <Trans>Please enter a shorter name (max: 50 characters)</Trans>)
    .required(<Trans>Please enter a name.</Trans>),
  base_image_url: Yup.string()
    .matches(/(https?:\/\/)[\d\w/.-_]*({{video_id}})[\d\w/.-_]*/, {
      message: "The URL must start with http:// or https:// and must contain {{video_id}}.",
      excludeEmptyString: true
    })
    .required(<Trans>Please enter a base image URL for this video host.</Trans>),
  base_video_url: Yup.string()
    .matches(/(https?:\/\/)[\d\w/.-_]*({{video_id}})[\d\w/.-_]*/, {
      message: "The URL must start with http:// or https:// and must contain {{video_id}}.",
      excludeEmptyString: true
    })
    .required(<Trans>Please enter a base video URL for this video host.</Trans>)
});

const VideoHostForm = ({ initialValues, onSuccess, onDelete }) => (
  <ApiConsumer>
    {({ callApi }) => (
      <Formik
        initialValues={{
          id: undefined,
          name: "",
          is_active: false,
          base_video_url: "",
          base_image_url: "",
          description: "",
          ...initialValues
        }}
        enableReinitialize
        validationSchema={VideoHostSchema}
        onSubmit={(values, { setSubmitting, setErrors, setStatus, resetForm }) => {
          callApi(() => (values.id ? updateVideoHost(values.id, values) : createVideoHost(values)))
            .then(res => {
              setSubmitting(false);

              if (onSuccess) {
                onSuccess(res.data);
              }

              toast.success(
                values.id ? <Trans>Video Host Updated!</Trans> : <Trans>Video Host created!</Trans>
              );
            })
            .catch(error => {
              setSubmitting(false);
              setErrors(transformMyRestApiErrorsToAnObject(error));
              toast.error(
                values.id
                  ? parseErrorMessage(error, <Trans>Failed to update Video Host.</Trans>)
                  : parseErrorMessage(error, <Trans>Video Host updated!</Trans>)
              );
            });
        }}>
        {({ isSubmitting, errors, status, values }) => (
          <div className="form-container">
            <h2 className="form-header">
              {values.id ? values.name : <Trans>Create a Brand New Video Host!</Trans>}
            </h2>
            <Form>
              <div className="form-input-group">
                <label className="form-label">
                  <Trans>Name</Trans>
                </label>
                <Field
                  className="form-input"
                  type="text"
                  autoFocus
                  autoComplete="off"
                  name="name"
                  disabled={isSubmitting}
                />
                <ErrorMessage name="name">
                  {errorMessage => <div className="form-input-error">{errorMessage}</div>}
                </ErrorMessage>
              </div>
              <div className="form-input-group">
                <label className="form-label">
                  <Trans>Base Iamge URL</Trans>
                </label>
                <Field
                  className="form-input"
                  type="text"
                  autoComplete="off"
                  name="base_image_url"
                  disabled={isSubmitting}
                />
                <ErrorMessage name="base_image_url">
                  {errorMessage => <div className="form-input-error">{errorMessage}</div>}
                </ErrorMessage>
              </div>
              <div className="form-input-group">
                <label className="form-label">
                  <Trans>Base Video URL</Trans>
                </label>
                <Field
                  className="form-input"
                  type="text"
                  autoComplete="off"
                  name="base_video_url"
                  disabled={isSubmitting}
                />
                <ErrorMessage name="base_video_url">
                  {errorMessage => <div className="form-input-error">{errorMessage}</div>}
                </ErrorMessage>
              </div>
              <div className="form-input-group">
                <label className="form-label">
                  <Trans>Description</Trans>
                </label>
                <Field
                  className="form-input"
                  component="textarea"
                  rows={5}
                  name="description"
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-input-group">
                <Field
                  component={Checkbox}
                  isSubmitting={isSubmitting}
                  name="is_active"
                  id="is_active"
                  label={<Trans>Do you want activate this video host?</Trans>}
                />
              </div>
              <div className="btn-group">
                {values.id && (
                  <DeleteResourceButton
                    isSubmitting={isSubmitting}
                    onDelete={onDelete}
                    url={`/video-hosts/${values.id}/`}
                  />
                )}

                <Button colour={`blue`} disabled={isSubmitting}>
                  {values.id && isSubmitting && <Trans>Updating Video Host</Trans>}
                  {values.id && !isSubmitting && <Trans>Update Video Host</Trans>}
                  {!values.id && isSubmitting && <Trans>Creating Video Host</Trans>}
                  {!values.id && !isSubmitting && <Trans>Create Video Host</Trans>}
                </Button>
              </div>
            </Form>
          </div>
        )}
      </Formik>
    )}
  </ApiConsumer>
);

export default VideoHostForm;
